import React from "react";
import { RADIUS_OPTIONS } from "./data.jsx";
import { useApp } from "./store.jsx";
import { LocationPicker } from "./location.jsx";
import { LanguageSwitch, GamePicker, CardLanguagePicker } from "./chrome.jsx";

const { Button, Wordmark, Icon, Card, Input, Select, Tag, Badge, FluteDivider, Checkbox } = window.CardboardmarketDesignSystem_2a3239;

/*
 * Account, location, games, card languages, wants.
 *
 * It was four until the languages needed a step of their own — sharing one with
 * the games put Continue below the fold on a phone, and a signup step whose
 * primary action is off screen is one people abandon rather than scroll. See the
 * step 4 block below for the measurements.
 */
/*
 * FOUR, not five. The fifth asked for a want before anybody had seen a single
 * card, and the owner cut it: "that's not really necessary and only adds
 * friction to the sign up."
 *
 * He is right, and the sharper version of why: a want typed at signup is a
 * guess made by somebody who has not yet looked at the market. Wants are asked
 * for properly in three places that know something — the Wants tab, the prompt
 * on a search that found nothing, and the import — and all three ask when the
 * person has a reason. A step that can be skipped with one tap still costs
 * everybody the tap and tells the ones who answer it the least useful thing.
 */
const STEPS = 4;

export function OnboardingScreen() {
  const { radius, setRadius, point, setPoint, games, setGames, languages, setLanguages, finishOnboarding, signup, login, notify, intent, trader, back, canBack, go , t, inviteCode, route } = useApp();
  /**
   * Step 1 creates an account. Somebody who already has one — a trader who
   * refreshed mid-setup, or who landed on /join by following an old link — must
   * not be asked to make a second one.
   */
  const [step, setStep] = React.useState(trader ? 2 : 1);
  /**
   * Signing in is the default — UNLESS they arrived by an invitation.
   *
   * The control that sends people here says "Sign in", so opening on a signup
   * form contradicts the button they just pressed. It is also the commoner case
   * by a wide margin after the first week: an account is made once and signed
   * into forever.
   *
   * SOMEBODY FOLLOWING AN INVITE LINK IS THE EXCEPTION, and not a marginal one.
   * They have no account by definition — a referral can only ever apply to a
   * new one — and they pressed a button that said "Join". Landing them on a
   * password field for an account they do not have is the product asking them
   * to remember something that does not exist. Reported by the owner: "it takes
   * me to the login screen, not the register one and it could be confusing."
   *
   * `inviteCode` is the honest signal, not the referrer of the click: it is set
   * by the landing page and cleared when it is spent, so it means exactly "this
   * person came here to make an account with somebody's code".
   *
   * AND `?mode=signup`, WHICH IS THE SAME SIGNAL WITHOUT A CODE.
   *
   * The invite case was the only one handled, and it left a hole exactly where
   * the money goes: an advert's landing page says "Crear mi cuenta", and
   * everybody who pressed it arrived at a form headed "Welcome back" asking for
   * a password to an account they have never had. Nobody carries an invite
   * code — there is nobody to be invited BY yet.
   *
   * Measured on production the evening this was found: EUR 71.82 spent, 905
   * clicks, and no signup at all for three days. The ad was not the problem; the
   * landing was.
   *
   * `mode` is already a query key this router understands, so an address can
   * carry it: the screen's own state has been called `mode` with these two
   * values since it was written, and a second key meaning the same thing would
   * be the drift this file has already been bitten by.
   */
  const [mode, setMode] = React.useState(
    route.mode === 'signup' || inviteCode ? 'signup' : 'login',
  );   // login | signup
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  /*
   * Typed twice, when making an account.
   *
   * Signing up signs you in, so a password typed wrong here is INVISIBLE: you
   * are in, you use the app, and the first sign of trouble is thirty days later
   * or on another device. A real trader lost his account that way — he was
   * signed in from the moment he joined and every sign-in since had failed,
   * which reads as "it suddenly stopped working" and is very hard to argue with.
   *
   * Only on signup. Asking somebody to type their existing password twice to
   * sign IN would be noise: they find out immediately either way.
   */
  const [confirm, setConfirm] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  /**
   * What to call the place they are marking, and it is OPTIONAL.
   *
   * A trader can save several locations now, and a name is what makes a list of
   * pins pickable — but on the way in there is only ever one, so a name earns
   * nothing yet and a required field here is a new toll on signing up. Left
   * alone, the server names it: a generic noun in the account's own language,
   * never a place name, because there is none to be had.
   *
   * Empty state rather than a filled-in default on purpose. A translated string
   * put into state freezes in whichever language loaded first — check-i18n
   * refuses it by name — so the words live on the server and the placeholder
   * only shows what will be used.
   */
  const [locationLabel, setLocationLabel] = React.useState('');
  const toggleGame = (g) => setGames(games.includes(g) ? games.filter((x) => x !== g) : [...games, g]);
  /*
   * The same toggle for card languages, and "All languages" is the empty list.
   *
   * Picking one clears All because All IS none-picked, and there is no third
   * state — so the two cannot end up lit at the same time however they are
   * clicked. Unpicking the last one lands back on All by itself.
   */
  const toggleLanguage = (code) => setLanguages(languages.includes(code) ? languages.filter((x) => x !== code) : [...languages, code]);

  /**
   * Step 1 creates the account, which is the only step that cannot be undone by
   * pressing Back — so it is the only one that talks to the server before the end.
   * Everything after it is a setting on an account that already exists.
   */
  const submitAccount = async () => {
    setBusy(true);
    try {
      if (mode === 'login') await login(email.trim(), password);
      else { await signup(email.trim(), password, name.trim()); setStep(2); }
    } catch (e) {
      notify(e.message, 'caution');
    } finally { setBusy(false); }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await finishOnboarding({ locationLabel });
    } catch (e) { notify(e.message, 'caution'); }
    finally { setBusy(false); }
  };

  // Setup is finished once there is a point to measure distances from. Anyone
  // ARRIVING here with one is done and belongs back in the app.
  //
  // Mount-only, and that is the whole point: finishing onboarding sets the location
  // too, so watching the value made this fire as a reaction to the last step —
  // going back once here and once in finishOnboarding, two entries, straight out of
  // the app. The question is "were you already set up when you got here", which is
  // only ever asked once.
  React.useEffect(() => {
    if (trader && trader.hasHomeLocation) back();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * Neither the games step nor the languages step asks for an answer.
   *
   * Step 3 used to require a game. That stopped being possible the moment "All
   * games" appeared on it: the chip stores an empty list, which is
   * indistinguishable from having chosen nothing — because it IS having chosen
   * nothing, rendered so a person can see it. A gate on `games.length` would
   * have left Continue disabled on an option the step offers, which is a dead
   * end nobody can diagnose from the screen. Step 4 never gated, for the same
   * reason spelled out where it renders.
   *
   * Step 2 still gates on a location, because a radius with no centre filters
   * nothing and there is no "everywhere" to fall back on. A preference has one.
   */
  const canNext =
    step === 1 ? email.includes('@') && password.length >= 12
                 && (mode === 'login' || (name.trim().length > 0 && confirm === password))
    : step === 2 ? Boolean(point)
    : true;

  return (
    <div style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', background: 'var(--bg-page)' }}>
      <div className="cbm-onb">
        <aside className="cbm-onb__brand">
          <Wordmark size={22} tone="inverse" />
          <h1 style={{ margin: 'auto 0 0', font: 'var(--type-display)', fontSize: 'clamp(38px,5vw,66px)', letterSpacing: 'var(--tracking-display)', color: 'var(--kraft-50)', textTransform: 'lowercase' }}>{t("cards near you, not in the post")}</h1>
          <p style={{ margin: '20px 0 0', font: 'var(--type-body)', color: 'var(--kraft-300)', maxWidth: 420 }}>{t("Set where you trade from and what you collect. We match you with holders a few streets away and you agree a place to meet.")}</p>
          <FluteDivider tone="brand" style={{ margin: '32px 0 20px' }} />
          <div style={{ display: 'flex', gap: 'var(--space-8)', flexWrap: 'wrap' }}>
            {[['1.6 km', t("median meetup")], ['0€', t("shipping paid")], [t("2 days"), t("median time to trade")]].map(([v, l]) => (
              <div key={l} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ font: 'var(--type-price)', fontSize: 'var(--text-lg)', color: 'var(--orange-300)' }}>{v}</span>
                <span className="cbm-label" style={{ color: 'var(--kraft-400)' }}>{l}</span>
              </div>
            ))}
          </div>
        </aside>

        <main className="cbm-onb__form">
          {/*
            A real <form>, not a div with a button.
            Enter submits because the browser does it, not because of a keydown
            handler somebody remembered to add — and a form with email and password
            inputs is also what tells a password manager to offer to fill and save.
            Every step submits: on the last one that is t("Start browsing"), on the
            others t("Continue").
          */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (busy) return;
              if (step === 1) return void submitAccount();
              if (step === STEPS) return void finish();
              if (canNext) setStep(step + 1);
            }}
            style={{ width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', gap: 'var(--space-7)' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
              <span className="cbm-meta">{t("Step {n} of {total}", { n: step, total: STEPS })}</span>
              {/* Before anything else: somebody reading the wrong language should
                  not have to finish signing up to fix it. */}
              <LanguageSwitch />
            </div>
            {step === 1 ? (
              <>
                <h2 className="cbm-h2">{mode === 'login' ? t("Welcome back") : t("Make an account")}</h2>
                <p className="cbm-sub" style={{ margin: 0 }}>
                  {intent
                    ? `You need an account to ${intent}. Browsing stays open without one.`
                    : mode === 'login'
                      ? t("Sign in and pick up where you left off.")
                      : t("You need one to list cards and message traders. Browsing works without.")}
                </p>
                {mode === 'signup' ? (
                  <Input label={t("Your name")} placeholder={t("What traders will see")} value={name} onChange={(e) => setName(e.target.value)} />
                ) : null}
                <Input label={t("Email")} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                <Input
                  label={t("Password")} type="password"
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  hint={mode === 'signup' ? t("At least 12 characters. Length beats punctuation.") : undefined}
                />
                {mode === 'signup' ? (
                  <Input
                    label={t("Password again")} type="password" autoComplete="new-password"
                    value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    /*
                     * Only once there is something to disagree with. Marking the
                     * field wrong while somebody is still typing the first
                     * character of it is a form telling you off for not having
                     * finished.
                     */
                    error={confirm && confirm !== password ? t("These two do not match.") : undefined}
                  />
                ) : null}
                <button
                  type="button"
                  onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
                  style={{ background: 'none', border: 0, padding: 0, textAlign: 'left', cursor: 'pointer', font: 'var(--type-meta)', color: 'var(--text-link)' }}
                >
                  {mode === 'login' ? t("No account yet? Make one.") : t("Already have an account? Sign in.")}
                </button>
                {/*
                  Only when signing in. Offering "forgot your password" beside a
                  SIGNUP form is offering to recover an account somebody is in
                  the middle of telling us they do not have.
                */}
                {mode === 'login' ? (
                  <button
                    type="button"
                    onClick={() => go('forgot')}
                    style={{ background: 'none', border: 0, padding: 0, textAlign: 'left', cursor: 'pointer', font: 'var(--type-meta)', color: 'var(--text-link)' }}
                  >
                    {t("Forgot your password?")}
                  </button>
                ) : null}
              </>
            ) : step === 2 ? (
              <>
                <h2 className="cbm-h2">{t("Where are you trading from?")}</h2>
                <p className="cbm-sub" style={{ margin: 0 }}>{t("We show listings you could walk or ride to. Other traders only ever see how far away you are, never where.")}</p>
                <LocationPicker value={point} onChange={setPoint} />
                {/*
                  The name, under the map rather than over it, and never gating
                  Continue — `canNext` still asks only for a point. It is the one
                  thing on this step that can be skipped entirely, so it sits
                  after the thing that cannot be.
                */}
                <Input
                  label={t("Name for this location")}
                  placeholder={t("My point")}
                  maxLength={40}
                  value={locationLabel}
                  onChange={(e) => setLocationLabel(e.target.value)}
                  hint={t("Only you ever see this. You can save more later.")}
                />
                <Select label={t("How far will you go?")} options={RADIUS_OPTIONS} value={radius} onChange={setRadius} hint={t("You can change this any time.")} />
              </>
            ) : step === 3 ? (
              <>
                <h2 className="cbm-h2">{t("What do you collect?")}</h2>
                <p className="cbm-sub" style={{ margin: 0 }}>{t("Pick everything you buy, sell or trade. It sets your default filters, and you can change them later.")}</p>
                {/*
                  "All games" first, and it is the empty list — the same control
                  the languages below have had since they arrived, now on the row
                  it was always missing from.

                  It is why this step no longer demands a game. `canNext` used to
                  require one, and once "All games" exists that requirement makes
                  the chip a lie: choosing it leaves `games` empty, which is
                  exactly what not choosing leaves, so the button would stay
                  disabled on a selection the interface offers. Either the chip
                  goes or the gate does, and the gate was the wrong one — "all"
                  is a real answer, and forcing a narrower one turns a default
                  into a toll on signing up.
                */}
                <GamePicker selected={games} onToggle={toggleGame} onAll={() => setGames([])} />
              </>
            ) : step === 4 ? (
              <>
                {/*
                  Card languages, on their OWN step — they used to share step 3
                  with the games.

                  Measured, not felt. Together on a Pixel 7 the step was 982px
                  tall against an 839px viewport and Continue sat 31px under the
                  fold, which on a signup step is the primary action being off
                  screen for everyone who does not think to scroll. Shortening
                  the language list to three chips and a reveal took 34px off and
                  was not enough on its own; splitting takes the step to exactly
                  839px, with Continue at 725px and no scrolling at all.

                  Two short steps beat one that has to be scrolled. It is a step
                  more in the counter, which is a real cost — but neither of them
                  demands an answer, so it is a step somebody passes with one tap.

                  Same rules as the games: not gated, "All languages" is the empty
                  list, and it is the answer somebody gives by walking past this.
                */}
                <h2 className="cbm-h2">{t("Which card languages?")}</h2>
                <p className="cbm-sub" style={{ margin: 0 }}>{t("The language printed on the card, not the one you are reading.")}</p>
                {/* The games step comes before this one, so by here we know
                    which languages are even possible. */}
                <CardLanguagePicker selected={languages} games={games} onToggle={toggleLanguage} onAll={() => setLanguages([])} />
                <span className="cbm-meta" style={{ color: 'var(--text-muted)' }}>{t("Leave it on all languages if you do not mind. You can change it later.")}</span>
              </>
            ) : null}
            {/*
              The same row on every step, and on the location step it FLOATS.

              Step 2 carries a map and a name field, so Continue sat about 400px
              under the fold on a Pixel 7 — 1252px down an 839px viewport, and
              1147px before the name field existed. A signup step whose primary
              action is off screen is one people abandon rather than scroll, and
              no amount of shorter copy moves an action sitting under 325px of
              controls.

              So it borrows the treatment the posting form and the listing page
              already use on a phone — `.cbm-actionbar` sticky against the bottom
              — rather than a second idea about the same problem. See app.css for
              the two things that differ: the breakpoint is onboarding's own 900px,
              and it rests lower, because there is no tab bar here to clear.

              Only step 2 wears it. The other three fit, and the panel would be
              chrome around a button that never needed lifting — which is also why
              this is not `.cbm-actionbar` itself: one row serves every step, so
              the app's class would have floated all of them.
            */}
            <div className={step === 2 ? 'cbm-onb__float' : undefined} style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
              <Button size="lg" type="submit" disabled={busy || (step !== STEPS && !canNext)}>
                {busy ? t("One moment\u2026")
                  : step === STEPS ? t("Start browsing")
                  : step === 1 ? (mode === 'login' ? t("Sign in") : t("Create account"))
                  : t("Continue")}
              </Button>
              {step > 2
                ? <Button size="lg" type="button" variant="ghost" onClick={() => setStep(step - 1)}>{t("Back")}</Button>
                : step === 1
                  ? <Button size="lg" type="button" variant="ghost" onClick={back}>{t("Keep browsing")}</Button>
                  : null}
            </div>
          </form>
        </main>
      </div>
    </div>
  );
}

